<template>
  <Teleport to="body">
    <Modal
      :model-value="modelValue"
      :canClose="true"
      :closeOnEsc="true"
      :closeOnBackdropClick="true"
      :title="isEditing ? $t('settings.candidate_fields.edit_title') : $t('candidate.fields.new.title')"
      @update:modelValue="emit('update:modelValue', $event)">
      <template #body>
        <p class="text-muted" v-if="!isEditing">{{ $t('candidate.fields.new.description') }}</p>

        <LeadInput
          v-model="label"
          class="mb-3"
          :label="$t('candidate.fields.new.label')"
          required
          :errors="labelErrors" />

        <LeadSelect
          v-model="type"
          class="mb-3"
          :label="$t('candidate.fields.new.type')"
          :options="typeOptions"
          :disabled="isEditing" />
        <p class="text-muted mb-3" v-if="isEditing">{{ $t('settings.candidate_fields.type_locked') }}</p>

        <div v-if="type === 'select'" class="mb-3">
          <label class="form-label">{{ $t('candidate.fields.new.options') }}</label>
          <div class="d-flex gap-2 mb-2" v-for="(choice, index) in choices" :key="index">
            <LeadInput v-model="choice.label" :placeholder="$t('candidate.fields.new.label')" class="flex-grow-1" />
            <LeadButton
              variant="icon"
              :icon-start="IconTrash"
              v-tooltip="$t('candidate.fields.new.remove_option')"
              @click="removeChoice(index)" />
          </div>
          <LeadButton variant="white" :icon-start="IconPlus" @click="addChoice">
            {{ $t('candidate.fields.new.add_option') }}
          </LeadButton>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 w-100">
          <LeadButton variant="white" :disabled="isSaving" @click="emit('update:modelValue', false)">
            {{ $t('btn.cancel') }}
          </LeadButton>
          <LeadButton variant="primary" :loading="isSaving" :disabled="!label.trim()" @click="save">
            {{ isEditing ? $t('btn.save') : $t('candidate.fields.new.create_button') }}
          </LeadButton>
        </div>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Modal } from '@prospective/lithium';
import { useToast } from 'vue-toastification';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import { createCandidateField, updateCandidateField } from '@/api/candidateFields';
import { useCandidateFieldsStore } from '@/stores/candidateFields';
import translator from '@/locale/translator';
import { IconPlus, IconTrash } from '@tabler/icons-vue';
import type { CandidateFieldDefinition, CandidateFieldOption, CandidateFieldType } from '@/types/candidate';

interface Props {
  modelValue: boolean;
  definition?: CandidateFieldDefinition | null;
  initialLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  definition: null,
  initialLabel: '',
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved', field: CandidateFieldDefinition): void;
}>();

const toast = useToast();
const store = useCandidateFieldsStore();

const label = ref('');
const type = ref<CandidateFieldType>('text');
const choices = ref<CandidateFieldOption[]>([]);
const isSaving = ref(false);
const labelErrors = ref<string[]>([]);

const isEditing = computed(() => Boolean(props.definition));

const typeOptions = [
  { label: translator.get('candidate.fields.type_text'), value: 'text' },
  { label: translator.get('candidate.fields.type_number'), value: 'number' },
  { label: translator.get('candidate.fields.type_date'), value: 'date' },
  { label: translator.get('candidate.fields.type_select'), value: 'select' },
];

// reset the form to reflect the target definition (or a blank create form) every time the modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;
    labelErrors.value = [];
    if (props.definition) {
      label.value = props.definition.label;
      type.value = props.definition.type;
      choices.value = props.definition.options?.choices.map((c) => ({ ...c })) ?? [];
    } else {
      label.value = props.initialLabel;
      type.value = 'text';
      choices.value = [];
    }
  },
  { immediate: true }
);

const addChoice = () => choices.value.push({ label: '', value: '' });
const removeChoice = (index: number) => choices.value.splice(index, 1);

// the value a select choice stores is derived from its label so recruiters only ever type one
// thing; slugifying keeps it short and stable even if the label is edited later
const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'option';

const save = async () => {
  if (!label.value.trim()) return;

  labelErrors.value = [];
  isSaving.value = true;
  try {
    const options =
      type.value === 'select'
        ? {
            choices: choices.value
              .filter((c) => c.label.trim())
              .map((c) => ({ label: c.label.trim(), value: c.value || slugify(c.label) })),
          }
        : null;

    const field = isEditing.value
      ? await updateCandidateField(props.definition!.field_key, { label: label.value.trim(), options })
      : await createCandidateField({ label: label.value.trim(), type: type.value, options });

    store.upsertDefinition(field);
    emit('saved', field);
    emit('update:modelValue', false);
  } catch (error) {
    console.error('Error saving candidate field definition:', error);
    toast.error(translator.get(isEditing.value ? 'candidate.fields.save_error' : 'candidate.fields.new.create_error'));
  } finally {
    isSaving.value = false;
  }
};
</script>
